import React from 'react'
import Layout from '../components/shared/Layout'
import { HeadFC, Link, PageProps, graphql } from 'gatsby'
import { SEO } from '../components/seo';
import { TbCalendar, TbChevronLeft } from 'react-icons/tb';
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { INLINES, BLOCKS, MARKS } from '@contentful/rich-text-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

export default function BlogPostTemplate(data:any) {
  
  const blog = data.data.blog;

  const options = {
    renderMark: {
      [MARKS.BOLD]: (text:any) => <b className="font-bold">{text}</b>,
    },
    renderNode: {
      [INLINES.HYPERLINK]: (node:any, children:any) => {
        const { uri } = node.data
        return (
          <a href={uri} target='_blank' className="underline">
            {children}
          </a>
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: (node:any) => {
        const { file } = node.data.target
        return <img src={file.url} className='rounded-xl w-full' />
      },
      [BLOCKS.HEADING_1]: (node:any, children:any) => {
        return <h2 className='text-6xl lg:text-7xl mt-8'>{children}</h2>
      },
      [BLOCKS.HEADING_2]: (node:any, children:any) => {
        return <h2 className='text-3xl lg:text-4xl mt-8'>{children}</h2>
      },
      [BLOCKS.HEADING_3]: (node:any, children:any) => {
        return <h3 className='text-2xl lg:text-3xl mt-8'>{children}</h3>
      },
      [BLOCKS.HEADING_4]: (node:any, children:any) => {
        return <h4 className='text-xl lg:text-xl mt-8'>{children}</h4>
      },
      [BLOCKS.HEADING_5]: (node:any, children:any) => {
        return <h4 className='text-lg lg:text-lg mt-8'>{children}</h4>
      },
      [BLOCKS.HEADING_6]: (node:any, children:any) => {
        return <h6 className='font-comico'>{children}</h6>
      },
      [BLOCKS.QUOTE]: (node:any, children:any) => {
        return <blockquote className='pl-4 py-2 border-l-4 border-grey italic'>{children}</blockquote>
      },
      [BLOCKS.TABLE]: (node:any, children:any) => {
        return <table className='table !rounded-xl border border-black/20 overflow-hidden w-full'>{children}</table>
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node:any, children:any) => {
        return <th className='p-2 font-bold bg-grey/60 border-4 border-white'>{children}</th>
      },
      [BLOCKS.TABLE_CELL]: (node:any, children:any) => {
        return <td className='p-2 text-center !border border-grey/80'>{children}</td>
      },
    }
  }

  return (
    <Layout
      data={data}
    >
      <section className='pt-32 lg:!pt-36 py-20 px-6 max-w-[900px] mx-auto flex flex-col gap-4 relative'>
        <div className='flex justify-between w-full'>
          <Link 
            to='/blogs' 
            className='flex items-center justify-center gap-2 cursor-pointer hover:font-bold'
          >
            <TbChevronLeft /> Go to blogs
          </Link>
        </div>
        <h1 className='text-5xl lg:text-7xl text-left'>{blog.title}</h1>
        <div className='text-lg'>{blog.subtitle}</div>
        <div className='flex items-center gap-4'>
          <div className='text-sm flex items-center gap-1'>
            <TbCalendar className='text-xl' /> Published {blog.createdAt}
          </div>
          <h4 className='font-comico text-center text-highlight-3'>{blog.topic}</h4>
        </div>
        <img src={blog.previewImage.file.url} className='mt-4 w-full !aspect-[5/3] object-cover rounded-xl' />
        <div className='text-lg mt-4 flex flex-col gap-6 blog'>
          {renderRichText(blog.content, options)}
        </div>
      </section>
    </Layout>
  )
}

export const Head: HeadFC = ({ pageContext }:any) => <SEO title={pageContext.title} description={pageContext.subtitle} image={pageContext.image} />

export const pageQuery = graphql`
  query MyQuery ($slug: String!) {
    blog: contentfulBlog(slug: {eq: $slug}) {
      createdAt(fromNow: true)
      previewImage {
        file {
          url
        }
      }
      slug
      subtitle
      title
      topic
      content {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            description
            gatsbyImageData(width: 1000)
            __typename
            file {
              url
            }
          }
        }
      }
    }
    headerFeatures: allContentfulFeature(filter: {showInHeader: {eq: true}}) {
      nodes {
        slug
        headerTitle
        headerDescription
        headerIcon {
          file {
            url
          }
        }
      }
    }
    footerFeatures: allContentfulFeature(filter: {showInFooter: {eq: true}}) {
      nodes {
        slug
        footerText
      }
    }
    headerIndustries: allContentfulIndustry(filter: {showInHeader: {eq: true}}) {
      nodes {
        slug
        headerTitle
        headerDescription
        headerIcon {
          file {
            url
          }
        }
      }
    }
    footerIndustries: allContentfulIndustry(filter: {showInFooter: {eq: true}}) {
      nodes {
        slug
        footerText
      }
    }
    footerCompares: allContentfulCompare(filter: {showInFooter: {eq: true}}) {
      nodes {
        slug
        footerText
      }
    }
  }
`;

